import styled from 'styled-components';

import { media } from '@/utils';

// ============================================================================
// LAYOUT COMPONENTS
// ============================================================================

export const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const Card = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  grid-template-rows: auto 1fr;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#242a33' : '#ffffff')};
  border: none;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  ${media.mobile} {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }
`;

export const Content = styled.div`
  padding: 24px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  ${media.mobile} {
    padding: 16px;
    align-self: flex-start;
  }
`;

// ============================================================================
// TAB COMPONENTS
// ============================================================================

export const TabsWrapper = styled.div`
  position: relative;
  overflow: hidden;

  ${media.mobile} {
    border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  }
`;

export const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#f9fafb')};
  border-right: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  overflow-y: auto;
  grid-row: 1 / -1;

  ${media.mobile} {
    background: transparent;
    border-right: none;
    grid-row: auto;
  }
`;

export const SidebarTitle = styled.div`
  padding: 16px 20px 8px;
  font-size: 15px;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#1f2937')};

  ${media.mobile} {
    display: none;
  }
`;

export const Tabs = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0 16px;

  ${media.mobile} {
    flex-direction: row;
    padding: 0;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
    border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
    gap: 0;
    white-space: nowrap;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

export const Tab = styled.button<{ $active?: boolean }>`
  padding: 12px 20px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ $active, theme }) => {
    if ($active) {
      return '#1a4d4d';
    }
    return theme.mode === 'dark' ? '#9ca3af' : '#6b7280';
  }};
  background: ${({ $active, theme }) => {
    if ($active) {
      return theme.mode === 'dark' ? '#374151' : '#f3f4f6';
    }
    return 'transparent';
  }};
  border: none;
  border-left: 3px solid ${({ $active }) => ($active ? '#E07B2D' : 'transparent')};
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
  text-align: left;

  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#f9fafb')};
    color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#1f2937')};
  }

  ${media.mobile} {
    padding: 12px 16px;
    font-size: 13px;
    border-left: none;
    border-bottom: 2px solid ${({ $active }) => ($active ? '#E07B2D' : 'transparent')};
    margin-bottom: -1px;
    flex-shrink: 0;
    text-align: center;
  }
`;

// ============================================================================
// HOTEL INFO COMPONENTS
// ============================================================================

export const HotelInfoSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 16px 18px;
  background: ${({ theme }) => (theme.mode === 'dark' ? theme.colors.background.card : '#ffffff')};
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;

  ${media.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
`;

export const HotelLogo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 6px;
  object-fit: cover;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  flex-shrink: 0;

  ${media.mobile} {
    width: 60px;
    height: 60px;
  }
`;

export const HotelDetails = styled.div`
  flex: 1;
  min-width: 0;
`;

export const HotelName = styled.h2`
  font-size: 20px;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#1f2937')};
  margin: 0 0 8px 0;

  ${media.mobile} {
    font-size: 18px;
  }
`;

export const HotelStars = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 8px;

  svg {
    width: 16px;
    height: 16px;
    fill: #fbbf24;
    color: #fbbf24;
  }
`;

export const HotelAddress = styled.p`
  font-size: 14px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  margin: 0;
  line-height: 1.5;

  ${media.mobile} {
    font-size: 13px;
  }
`;

// ============================================================================
// SECTION COMPONENTS
// ============================================================================

export const LanguagesSection = styled.div`
  margin-top: 0;
`;

export const TeamSection = styled.div`
  margin-top: 0;
`;

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#1f2937')};
  margin: 0 0 8px 0;

  ${media.mobile} {
    font-size: 15px;
  }
`;

export const SectionDescription = styled.p`
  font-size: 13px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  margin: 0 0 16px 0;
  line-height: 1.5;

  ${media.mobile} {
    font-size: 12px;
  }
`;

export const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  font-size: 14px;
`;

// ============================================================================
// LANGUAGE COMPONENTS
// ============================================================================

export const LanguagesList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;

  ${media.mobile} {
    grid-template-columns: 1fr;
  }
`;

export const LanguageItem = styled.button<{ $isDefault?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${({ theme, $isDefault }) => {
    if ($isDefault) return theme.mode === 'dark' ? '#374151' : '#f3f4f6';
    return theme.mode === 'dark' ? '#1f2937' : '#ffffff';
  }};
  border: 1px solid ${({ theme, $isDefault }) => {
    if ($isDefault) return '#1a4d4d';
    return theme.mode === 'dark' ? '#374151' : '#e5e7eb';
  }};
  border-radius: 6px;
  transition: all 0.2s;
  cursor: ${({ $isDefault }) => ($isDefault ? 'default' : 'pointer')};
  width: 100%;
  text-align: left;

  &:hover {
    border-color: ${({ theme, $isDefault }) => {
    if ($isDefault) return '#1a4d4d';
    return theme.mode === 'dark' ? '#4b5563' : '#d1d5db';
  }};
    background: ${({ theme, $isDefault }) => {
    if ($isDefault) return theme.mode === 'dark' ? '#374151' : '#f3f4f6';
    return theme.mode === 'dark' ? '#374151' : '#f9fafb';
  }};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const FlagWrapper = styled.div`
  width: 32px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${media.mobile} {
    width: 28px;
    height: 20px;
  }
`;

export const LanguageCode = styled.span`
  font-size: 14px;
  font-weight: 500;
  text-transform: uppercase;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#1f2937')};
  min-width: 30px;

  ${media.mobile} {
    font-size: 13px;
  }
`;

export const LanguageName = styled.span`
  font-size: 14px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  flex: 1;

  ${media.mobile} {
    font-size: 13px;
  }
`;

export const DefaultBadge = styled.span`
  position: absolute;
  top: -6px;
  left: 8px;
  font-size: 8px;
  font-weight: 300;
  padding: 1px 4px;
  background: #1a4d4d;
  color: white;
  border-radius: 6px;
  text-transform: uppercase;
  white-space: nowrap;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  line-height: 1.2;

  ${media.mobile} {
    font-size: 7px;
    padding: 1px 3px;
    top: -5px;
    left: 6px;
  }
`;

// ============================================================================
// TABLE COMPONENTS
// ============================================================================

export const TeamTable = styled.div`
  overflow-x: auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
`;

export const TableHeader = styled.thead`
  ${media.mobile} {
    display: none;
  }
`;

export const TableHeaderCell = styled.th<{ align?: 'left' | 'center' | 'right' }>`
  padding: 16px 20px;
  text-align: ${(props) => props.align || 'left'};
  font-size: 13px;
  font-weight: 300;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#d1d5db' : '#334155')};
  background: ${({ theme }) => (theme.mode === 'dark' ? '#242a33' : '#f1f5f9')};
  white-space: nowrap;

  &:first-child {
    border-top-left-radius: 10px;
    border-bottom-left-radius: 10px;
  }

  &:last-child {
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
  }
`;

export const TableBody = styled.tbody`
  td {
    border-bottom: 1px solid ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#e5e7eb')};
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

export const TableRow = styled.tr`
  &:hover {
    background: ${({ theme }) => (theme.mode === 'dark' ? '#1f2937' : '#f9fafb')};
  }
`;

export const TableCell = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#1f2937')};
  text-align: left;

  &:first-child {
    padding-left: 20px;
  }

  &:last-child {
    padding-right: 20px;
  }
`;

export const UserName = styled.div`
  font-weight: 500;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#f3f4f6' : '#1f2937')};
`;

export const UserEmail = styled.div`
  font-size: 13px;
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  margin-top: 2px;
`;

export const RoleBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#374151' : '#f3f4f6')};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
`;

export const StaffAppIcon = styled.div<{ $hasAccess?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ $hasAccess }) => ($hasAccess ? '#E07B2D' : '#9ca3af')};
  }
`;

export const PermissionsIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
    color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
  }
`;

export const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
